import React, { createContext, useContext, useEffect, useReducer } from 'react';
import { useAuth } from '@guildeducationinc/guild-auth';
import { EmployerContent } from '@shared/interfaces/EmployerContent';
import useEmployerService from '@src/hooks/useEmployerService';
import { Link } from '@src/utilities/componentInjectUtility';
import { contentReducer, fetchHomeContentModule } from '@src/utilities/getEmployerContent';
import { ComponentDataKey } from '../components/Pages/Home/Layout/config';
import { useMemberPlanContext } from './MemberPlanContext';

type ReferralForm = EmployerContent & {
  id?: string;
  formLabel?: string;
  subheading?: string;
};

type Funding = EmployerContent & {
  cta?: {
    body: string;
    href: string;
  };
};

interface EmployerContentContext {
  coach_contact_card?: CoachContactCard;
  eligibility?: Eligibility;
  eligibility_snapshot?: EligibilitySnapshot;
  funding?: Funding;
  general_contact_card?: GeneralContactCard;
  learn_skills?: EmployerContent;
  referral_form?: ReferralForm;
  resources?: EmployerContent;
  student_loan?: StudentLoan;
  your_classes?: EmployerContent;
  career_resources?: EmployerContent;
  career_pathways?: EmployerContent;
  loading: boolean;
  error: boolean;
}

interface Eligibility extends EmployerContent {
  tooltip: string;
}

interface EligibilitySnapshot {
  status: {
    NOT_VERIFIED: EligibilityStatus;
    ELIGIBLE: EligibilityStatus;
    INELIGIBLE: EligibilityStatus;
    CONNECTION_ERROR: EligibilityStatus;
    NOT_FOUND: EligibilityStatus;
  };
}

interface EligibilityStatus {
  title: string;
  link?: Link;
  verifyHref?: string;
  verifyHrefCopy?: string;
}

interface StudentLoan extends EmployerContent {
  card: {
    title: string;
    subtitle: string;
    body: string;
    modalButtonCopy: string;
    ssoLink: string;
    ssoLinkCopy: string;
  };
  modal: {
    title: string;
    description: {
      optimization: string;
      reimbursement: string;
    };
    button: {
      text: string;
      href: string;
    };
  };
  providerCallout: {
    img: {
      alt: string;
      src: string;
    };
    text: string;
  };
  reimbursementCallout: {
    title: string;
    body: string;
    button: {
      text: string;
      href: string;
    };
  };
}

interface GeneralContactCard extends EmployerContent {
  heading: string;
  description: string;
}

interface CoachContactCard extends EmployerContent {
  heading: string;
  description: string;
}

const EmployerContentContext = createContext<EmployerContentContext | undefined>(undefined);

const EmployerContentProvider: React.FC = ({ children }) => {
  const { user } = useAuth();
  const { memberPlan } = useMemberPlanContext();
  const { employer } = useEmployerService(user?.employerId);

  const initialState: EmployerContentContext = {
    loading: true,
    error: false,
  };
  const [employerContent, setEmployerContent] = useReducer(contentReducer, initialState);

  useEffect(() => {
    const moduleNames = [
      ...Object.keys(ComponentDataKey).map((key) => ComponentDataKey[key]),

      // Add any additional module names that are not part of the Component Data keys here
      'eligibility_snapshot',
    ];

    const fetchEmployerContent = async () => {
      const { employerId } = user ?? {};

      try {
        const contentModules = await Promise.all(
          moduleNames.map((moduleName) =>
            fetchHomeContentModule<EmployerContent>(employerId ?? 'default', moduleName, {
              has_no_coverage: memberPlan?.hasNoCoverage,
              employerName: employer?.name,
              employerNickname: employer?.shortName,
            })
          )
        );

        const newEmployerContent = contentModules.reduce((content, contentModule) => {
          return {
            ...content,
            ...contentModule,
          };
        }, {});

        setEmployerContent({ type: 'success', payload: newEmployerContent });
      } catch (error) {
        setEmployerContent({ type: 'failure', error });
      }
    };

    if (user) {
      fetchEmployerContent();
    }
  }, [user, memberPlan?.hasNoCoverage, employer?.name, employer?.shortName]);

  return (
    <EmployerContentContext.Provider value={{ ...employerContent }}>
      {children}
    </EmployerContentContext.Provider>
  );
};

/**
 *
 * Serves as the content fetcher
 *
 * ```ts
 * import { useEmployerContent } from '@src/context/EmployerContentContext';
 *
 * const Component = () => {
 *    const key = ComponentDataKey.['YOUR DATA KEY'] || 'Your Generic Key Here';
 *    const { [key]: content, error, loading } = useEmployerContent();
 *    ...
 * }
 * ```
 * @packageDocumentation
 */
const useEmployerContent = () => {
  const context = useContext(EmployerContentContext);

  /* istanbul ignore if */
  if (context === undefined) {
    throw new Error('useEmployerContent must be called within an EmployerContentProvider.');
  }

  return { ...context };
};

export default EmployerContentProvider;
export { useEmployerContent, EmployerContentContext, EligibilityStatus, EligibilitySnapshot };
